import { PlusIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { SketchPicker } from "react-color";
import { useDispatch, useSelector } from "react-redux";
import { ClipboardList as CaptionIcon } from "solar-icon-set/notes";
import { GalleryRound as GalleryIcon } from "solar-icon-set/videoaudiosound";
import RedCrossIcon from "../../../../assets/images/RedCross.svg";
import StarsImg from "../../../../assets/images/stars.png";
import UNIVERSAL from "../../../../config/config";
import { creativeIDSplit } from "../../../../constants/ads";
import { generateCreativeID } from "../../../../libs/utils";
import { CreativeCanvas } from "../../../LiveAds/Canvas/CreativeCanvas";
import Step3Card from "../../StepsCard/Step3Card/Step3Card";
import "../CampaignStep.css";
import "./Modal.css";
import { PuffLoader } from "react-spinners";
import { Popover } from "@mui/material";

const CampaignStep3 = ({ templates, creatives, fetchHeadlines, userData, fetchColors, fetchCaptions, setCreatives, brandData, isLoading }) => {
  const step2 = useSelector((state) => state.steps.step2Data);
  const business = useSelector((state) => state.business.data);
  const selectedFbPage = useSelector((state) => state.fb.selectedFbPage);
  const user = useSelector((state) => state?.user?.user);

  const [showCustimize, setShowCustimize] = useState(false);
  const [selectedCustimizeCard, setSelectedCustimizeCard] = useState(0);
  const [selectedCustimize, setSelectedCustimize] = useState("Creative");
  const [text, setText] = useState();
  const [imageFile, setImageFile] = useState("");
  const [s3ImageLink, setS3ImageLink] = useState();
  const [uploadedImageFileName, setUploadedImageFileName] = useState("");
  const [adContent, setAdContent] = useState("");
  const [cta, setCta] = useState("");
  const [showColorSelector, setShowColorSelector] = useState(false);
  const [selectedColor, setSelectedColor] = useState();
  const [updatedCreativesData, setUpdatedCreativesData] = useState();
  const [headlineSuggestions, setHeadlineSuggestion] = useState();
  const [adCaptionSuggestion, setAdCaptionSuggestion] = useState();
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [showAdCaptionSpinner, setShowAdCaptionSpinner] = useState(false);
  const [showAutoSuggestionSpinner, setShowAutoSuggestionSpinner] = useState(false);
  const [showImageUploadSpinner, setShowImageUploadSpinner] = useState(false);
  const [initialData, setInitialData] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentColor, setCurrentColor] = useState("");

  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoading) {
      setCreatives(undefined);
    }
  }, [isLoading]);

  const compatibleTemplates = useMemo(() => {
    const _templates = creatives?.all
      ?.filter((creative) => creative.image === creatives?.selected?.[selectedCustimizeCard]?.image && creative.score >= 0)
      .map((creative) => templates?.find((template) => template.id === creative.template));
    return _templates && _templates.length > 0 ? _templates : templates?.slice(0, 7);
  }, [creatives, selectedCustimizeCard, templates]);

  function handleCustimize() {
    setShowCustimize(true);
  }

  useEffect(() => {
    if (creatives) {
      handleSelectedCustimizeCard(selectedCustimizeCard);
      setText(creatives?.selected?.[selectedCustimizeCard]?.headline);
      setAdContent(creatives?.selected?.[selectedCustimizeCard]?.caption);
      setCta(creatives?.selected?.[selectedCustimizeCard]?.cta);
    }
  }, [creatives]);

  function handleSelectedCustimizeCard(selected) {
    setSelectedCustimizeCard(selected);
    if (updatedCreativesData?.[selected]) {
      setText(updatedCreativesData?.[selected]?.headline);
      setAdContent(updatedCreativesData?.[selected]?.caption);
      setCta(updatedCreativesData?.[selected]?.cta);
      handleUpdateData(selected);
    }
  }

  function handleUpdateData(selected) {
    if (updatedCreativesData?.length > 0) {
      const newData = updatedCreativesData?.map((creative) => {
        if (creative.id === updatedCreativesData?.[selected]?.id) {
          return { ...creative, headline: text, caption: adContent, cta: cta };
        }
        return creative;
      });
      setUpdatedCreativesData(newData);
    }
  }

  useEffect(() => {
    handleUpdateData(selectedCustimizeCard);
  }, [text, adContent, cta, selectedColor]);

  function handleSelectedCustomization(selected) {
    setSelectedCustimize(selected);
  }

  function handleTextChange(event) {
    setText(event.target.value);
  }

  function handleImageFile(event) {
    const file = event.target.files[0];
    setUploadedImageFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageFile(reader.result.toString());
    };
    reader.readAsDataURL(file);
  }

  function RemoveUploadedImage() {
    const newData = updatedCreativesData?.map((creative) => {
      if (creative.id === updatedCreativesData?.[selectedCustimizeCard]?.id) {
        return { ...creative, headline: text, caption: adContent, cta: cta, background: selectedColor, custom_image: undefined };
      }
      return creative;
    });
    setUpdatedCreativesData(newData);
  }

  useEffect(() => {
    if (imageFile && uploadedImageFileName) {
      async function getImageS3Url() {
        setShowImageUploadSpinner(true);
        return fetch(UNIVERSAL.BASEURL_1 + "/internal/upload_image", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "6gZ2J0WfUx9iAmKIwvwG3NiX1783yn094tzrssHe",
          },
          body: JSON.stringify({
            filename: uploadedImageFileName,
            image: imageFile.substring(22),
          }),
        })
          .then((response) => response.json())
          .then((responseJson) => {
            if (responseJson) {
              setS3ImageLink(responseJson.url);
              const newData = updatedCreativesData?.map((creative) => {
                if (creative.id === updatedCreativesData?.[selectedCustimizeCard]?.id) {
                  return { ...creative, headline: text, caption: adContent, cta: cta, background: selectedColor, custom_image: responseJson.url };
                }
                return creative;
              });
              setUpdatedCreativesData(newData);
              setShowImageUploadSpinner(false);
            } else {
              // error
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
      getImageS3Url();
    }
  }, [imageFile]);

  const selectedUpdatedTemplate = templates?.find((template) => template.id === updatedCreativesData?.[selectedCustimizeCard]?.template);

  const handleSuggestions = async () => {
    setShowAutoSuggestionSpinner(true);
    const suggestions = await fetchHeadlines.mutateAsync({
      name: step2.name,
      description: step2.description,
      selling_price: step2.selling_price,
      currency: step2.currency,
      limit: 3,
    });
    setHeadlineSuggestion(suggestions);
    setShowAutoSuggestionSpinner(false);
  };

  useEffect(() => {
    const handleSuggestions = async () => {
      setShowAutoSuggestionSpinner(true);
      const suggestions = await fetchHeadlines.mutateAsync({
        name: step2?.name,
        description: step2?.description,
        selling_price: step2?.selling_price,
        currency: step2?.currency,
        limit: 3,
      });
      setHeadlineSuggestion(suggestions);
      setShowAutoSuggestionSpinner(false);
    };
    handleSuggestions();
  }, []);

  useEffect(() => {
    if (creatives) {
      setUpdatedCreativesData(creatives?.selected);
    }
  }, [creatives]);

  function handleSaveChanges(e) {
    e.preventDefault();
    setShowCustimize(false);
    setCreatives((state) => ({ ...state, selected: updatedCreativesData }));
  }

  function handleSetColor(hex) {
    setCreatives((state) => ({ ...state, selected: state.selected.map((c, index) => (index === selectedCustimizeCard ? { ...c, background: hex } : c)) }));
    setCurrentColor(hex);
  }

  async function handleAdCaptionSuggestion() {
    setShowAdCaptionSpinner(true);
    try {
      const newSuggestion = await fetchCaptions.mutateAsync({
        name: step2.name,
        description: step2.description,
        selling_price: step2.selling_price,
        currency: step2.currency,
        limit: 1,
      });
      if (newSuggestion?.length) {
        setAdContent(newSuggestion[0]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowAdCaptionSpinner(false);
    }
  }

  return (
    <>
      {showCustimize ? (
        <div className="customizeDiv">
          <div className="customizeDiv__left">
            <Step3Card
              key={updatedCreativesData?.[selectedCustimizeCard].id}
              data={updatedCreativesData?.[selectedCustimizeCard]}
              pageName={userData?.facebook_page_name}
              showPreviewRibbon
            >
              <CreativeCanvas creative={updatedCreativesData?.[selectedCustimizeCard]} template={selectedUpdatedTemplate} showPreviewRibbon />
            </Step3Card>
            <div className="customizeDiv__left__cardNumberCircle">
              <span className={selectedCustimizeCard === 0 ? "selected" : ""} onClick={() => handleSelectedCustimizeCard(0)}>
                1
              </span>
              <span className={selectedCustimizeCard === 1 ? "selected" : ""} onClick={() => handleSelectedCustimizeCard(1)}>
                2
              </span>
              <span className={selectedCustimizeCard === 2 ? "selected" : ""} onClick={() => handleSelectedCustimizeCard(2)}>
                3
              </span>
            </div>
          </div>
          <div className="customizeDiv__right">
            <div className="crossDiv">
              <img className="crossImg" src="https://d1n7zmj0fa1o7h.cloudfront.net/instamojo-assets/close-circle.svg" alt="cross-icon" onClick={() => setShowCustimize(false)} />
            </div>
            <div className="custimizeDiv__rightInner">
              <h4>Ad customization</h4>
              <p>Customise your ad and get the templates accordingly</p>
              <div className="toggleCaptionCreative">
                <span className={selectedCustimize === "Creative" ? "selected" : ""} onClick={() => handleSelectedCustomization("Creative")}>
                  <GalleryIcon iconStyle="BoldDuotone" size={24} /> Creative
                </span>
                <span className={selectedCustimize === "Caption" ? "selected" : ""} onClick={() => handleSelectedCustomization("Caption")}>
                  <CaptionIcon iconStyle="BoldDuotone" size={24} /> Caption
                </span>
              </div>
              {selectedCustimize === "Caption" ? (
                <>
                  <textarea
                    aria-label="Captions"
                    class="form-control textModal-textInput"
                    id="exampleFormControlTextarea1"
                    rows="6"
                    placeholder="A sweet, baked, breadlike food, made with or without shortening and usually containing flour, sugar, baking powder or soda, eggs, and liquid flavoring."
                    value={text}
                    name="text"
                    onChange={handleTextChange}
                  ></textarea>
                  <div className="textModal-autoSuggestion">
                    <span className="textModal-autoSuggestionHeading">
                      <p className="textModal-autoSuggestion-heading">Auto Suggestions</p>
                      {!showAutoSuggestionSpinner && (
                        <img
                          className="textModal-autoSuggestion-headingImg"
                          src="https://d1n7zmj0fa1o7h.cloudfront.net/instamojo-assets/rotate-right.svg"
                          alt="auto-suggestion-icon"
                          onClick={handleSuggestions}
                        />
                      )}
                      {showAutoSuggestionSpinner && <PuffLoader size={30} color="#0F6EFF" />}
                    </span>
                    <div className="textModal-autoSuggestion-examples">
                      {headlineSuggestions &&
                        headlineSuggestions.map((headline, index) => {
                          return (
                            <div className="textModal-autoSuggestion-example" key={index} onClick={() => setText(headline)}>
                              <div>{headline}</div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {updatedCreativesData?.[selectedCustimizeCard].custom_image ? (
                    <>
                      {!showImageUploadSpinner && (
                        <div className="s3UploadedImage">
                          <img className="s3Img" src={updatedCreativesData?.[selectedCustimizeCard].custom_image} alt="step3-img1" />
                          <img className="s3ImgRedCrossIcon" onClick={RemoveUploadedImage} src={RedCrossIcon} alt="redcross-icon" />
                        </div>
                      )}
                      {showImageUploadSpinner && <PuffLoader size={30} color="#0F6EFF" />}
                    </>
                  ) : (
                    <>
                      <h1></h1>
                      <div className="imageModalUploadImage">
                        <input className="imageModalUploadImage-file" type="file" name="imageFile" onChange={(e) => handleImageFile(e)} id="file-upload" />
                        <label className="imageModalUploadImage-btn" for="file-upload">
                          <div className="imageModalUploadImageIconDiv">
                            <img
                              className="imageModalUploadImage-icon"
                              src="https://d1n7zmj0fa1o7h.cloudfront.net/instamojo-assets/send-square.svg"
                              alt="imageModalUploadImage-icon"
                            />
                          </div>
                          Drop your own ad creative here
                        </label>
                      </div>
                      <div className="creativeForm">
                        <div className="creativeForm__line">
                          <hr />
                          or edit these details
                          <hr />
                        </div>
                        <div className="creativeForm__adContent">
                          <input
                            className="adContentInput"
                            type="text"
                            name="adContent"
                            placeholder="Ad Content"
                            value={adContent}
                            onChange={(e) => setAdContent(e.target.value)}
                          />
                          {!showAdCaptionSpinner && <img className="adContentImg" onClick={handleAdCaptionSuggestion} src={StarsImg} alt="stars-img" />}
                          {showAdCaptionSpinner && <PuffLoader size={30} color="#0F6EFF" />}
                        </div>
                        <input className="ctaInput" type="text" name="cta" placeholder="CTA" value={cta} onChange={(e) => setCta(e.target.value)} />
                        <div className="chooseColor">
                          <p>Choose your color</p>
                          <div className="chooseColorDiv">
                            {(brandData?.primary_colors ? JSON.parse(brandData.primary_colors) : fetchColors?.data?.primary_color_list).slice(0, 3).map((hex, index) => {
                              return (
                                <div
                                  className={`btnDiv ${updatedCreativesData?.[selectedCustimizeCard]?.background?.toLowerCase() === hex?.toLowerCase() ? "selectedBtnDiv" : ""}`}
                                  key={hex + index}
                                  onClick={() => handleSetColor(hex, index)}
                                >
                                  <button style={{ backgroundColor: hex, border: "1px solid gray" }}></button>
                                </div>
                              );
                            })}
                            <button
                              className="selectColor"
                              onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                setShowColorSelector(true);
                              }}
                            >
                              <PlusIcon size={14} strokeWidth={2.5} />
                            </button>
                            {showColorSelector && (
                              <Popover
                                open={showColorSelector && anchorEl}
                                anchorEl={anchorEl}
                                onClose={() => setShowColorSelector(false)}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                              >
                                <div className="colorSelector">
                                  <SketchPicker color={currentColor} onChangeComplete={(color) => handleSetColor(color.hex, "")} />
                                </div>
                              </Popover>
                            )}
                          </div>
                          <div className="creativeTemplates">
                            <p>Templates</p>
                            <div className="creativeTemplates__templates">
                              {compatibleTemplates.map((template) => {
                                const id = generateCreativeID(template.name, creatives?.selected?.[selectedCustimizeCard].id.split(creativeIDSplit).at(1));
                                const existing = creatives?.all?.find((creative) => creative.id === id);
                                const creative = { ...creatives?.selected?.[selectedCustimizeCard], template: template.id, resize: existing?.resize, score: existing?.score ?? -1 };
                                const isActive =
                                  creatives?.selected?.[selectedCustimizeCard].template === template.id && !creatives?.selected?.[selectedCustimizeCard].custom_image;
                                return (
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    style={{ display: "flex", width: "100%", aspectRatio: 1 / 1, position: "relative", border: "1px solid lightgray", borderRadius: "8px" }}
                                    key={template.id}
                                    onClick={() => {
                                      setCreatives((state) => ({ ...state, selected: state.selected.map((c) => (c.id === creative.id ? creative : c)) }));
                                    }}
                                  >
                                    <CreativeCanvas creative={creative} template={template} />
                                    {isActive && <img className="selectedTemplate" src="/assets/icons/tickCircleFilled.svg" />}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="saveChanges">
              <button onClick={handleSaveChanges}>Save Changes</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {creatives && userData?.profileUrl && creatives?.selected ? (
            <>
              <div className="campaignStep3">
                {creatives &&
                  creatives.selected &&
                  creatives?.selected.map((data) => {
                    const template = templates.find((template) => template.id === data.template);
                    return (
                      <Step3Card key={data.id} data={data} pageName={userData?.facebook_page_name} showCircularBar>
                        <CreativeCanvas creative={data} template={template} />
                      </Step3Card>
                    );
                  })}
              </div>
              <p className="custimize">
                🎨 Need customisations in the above creatives <span onClick={handleCustimize}>click here</span>
              </p>
            </>
          ) : (
            <div className="puffLoadingSpinner">
              <PuffLoader size={100} color="#0F6EFF" />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CampaignStep3;
